import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../commonDesign/Button'
import FlexRow from '../../../../../molecules/FlexRow'

function RawDefinitionEditor ({ definition, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(definition ?? {}, null, 2))
  const [error, setError] = useState(null)
  const [hasBlurred, setHasBlurred] = useState(false)
  const [applied, setApplied] = useState(false)
  const latestTextRef = useRef(text)
  const appliedTimerRef = useRef(null)
  const pendingApplyRef = useRef(false)

  useEffect(() => {
    const incoming = JSON.stringify(definition ?? {}, null, 2)
    if (incoming !== latestTextRef.current && !error) {
      setText(incoming)
      latestTextRef.current = incoming
    }
  }, [definition]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((newText) => {
    setText(newText)
    latestTextRef.current = newText
    try {
      JSON.parse(newText)
      setError(null)
    } catch {
      if (hasBlurred) {
        setError('Invalid JSON')
      }
    }
  }, [hasBlurred])

  const handleBlur = useCallback(() => {
    setHasBlurred(true)
    if (pendingApplyRef.current) return
    try {
      const parsed = JSON.parse(text)
      setError(null)
      onChange(parsed)
    } catch {
      setError('Invalid JSON')
    }
  }, [text, onChange])

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(text)
      const formatted = JSON.stringify(parsed, null, 2)
      setText(formatted)
      latestTextRef.current = formatted
      setError(null)
    } catch {
      setError('Cannot format -- invalid JSON')
    }
  }, [text])

  const handleApplyMouseDown = useCallback(() => {
    pendingApplyRef.current = true
  }, [])

  const handleApply = useCallback(() => {
    pendingApplyRef.current = false
    try {
      const parsed = JSON.parse(text)
      setError(null)
      onChange(parsed)
      setApplied(true)
      if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current)
      appliedTimerRef.current = setTimeout(() => setApplied(false), 1500)
    } catch {
      setError('Cannot apply -- invalid JSON')
    }
  }, [text, onChange])

  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
      if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current)
    }
  }, [])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2000)
    })
  }, [text])

  const lineCount = text ? text.split('\n').length : 0

  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-color-subtle)', margin: '0 0 8px' }}>
        Edit the full definition JSON directly. Changes are applied on blur or when you click Apply.
      </p>
      <AceEditor
        mode='json'
        theme='xcode'
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        width='100%'
        height='500px'
        fontSize={12}
        showPrintMargin={false}
        showGutter
        highlightActiveLine
        setOptions={{
          showLineNumbers: true,
          tabSize: 2,
          useWorker: false
        }}
        editorProps={{ $blockScrolling: true }}
      />
      <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
        <FlexRow style={{ gap: '12px', alignItems: 'center' }}>
          {error && (
            <span style={{ color: 'var(--color-danger-500)', fontSize: '0.75rem' }}>
              {error}
            </span>
          )}
          <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
            {lineCount} lines
          </span>
        </FlexRow>
        <FlexRow style={{ gap: '8px' }}>
          <SydButton variant='ghost' size='sm' onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy JSON'}
          </SydButton>
          <SydButton variant='outline' size='sm' onClick={handleFormat}>
            Format JSON
          </SydButton>
          <span onMouseDown={handleApplyMouseDown}>
            <SydButton variant='primary' size='sm' onClick={handleApply} disabled={!!error}>
              {applied ? 'Applied!' : 'Apply Changes'}
            </SydButton>
          </span>
        </FlexRow>
      </FlexRow>
    </div>
  )
}

RawDefinitionEditor.propTypes = {
  definition: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

export default RawDefinitionEditor
